import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Link,
} from "@mui/material";
import { Trash } from "@phosphor-icons/react";
import Papa, { ParseResult } from "papaparse";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ShotListType } from "../../../types/public-types";
import useStyles from "./ModalStyles";
import { bulkUploadShotsApiwithBreakdownId, bulkUploadShotsApi } from "../../../Services/breakdownServices/breakdownServices";

/** Wait after last keystroke/paste before parsing (avoids errors while paste is in flight). */
const CSV_PARSE_DEBOUNCE_MS = 350;

/** Header row for the downloadable example CSV (matches instructional copy). */
const BULK_IMPORT_CSV_EXAMPLE_HEADERS = [
  "description",
  "characters",
  "cameraPosition",
  "location",
  "dialogue",
  "shotEmotion",
  "voiceOver",
  "storyboardPrompt",
  "shotType",
  "rackFocus",
  "vfxRequired",
  "cameraAngle",
  "focalLength",
  "characterAppearance",
] as const;

function downloadBulkImportExampleCsv() {
  const csv = `${BULK_IMPORT_CSV_EXAMPLE_HEADERS.join(",")}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "example.csv";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type DynamicCsvRow = {
  id: string;
  /** Cell values in the same order as `columnHeaders` */
  cells: string[];
};

function stripBomKeys(raw: Record<string, unknown>) {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    out[k.replace(/^\uFEFF/, "")] = v;
  }
  return out;
}

/** One object key per column; duplicate header labels get ` (2)`, ` (3)`, … */
function uniqueKeysFromHeaders(headers: string[]): string[] {
  const counts = new Map<string, number>();
  return headers.map((h, i) => {
    const base = h.trim() || `Column ${i + 1}`;
    const n = (counts.get(base) ?? 0) + 1;
    counts.set(base, n);
    return n === 1 ? base : `${base} (${n})`;
  });
}

/** Each shot is `{ [columnHeader]: cellValue, ... }` aligned with the table. */
function rowToShotObject(headers: string[], row: DynamicCsvRow): Record<string, string> {
  const keys = uniqueKeysFromHeaders(headers);
  const out: Record<string, string> = {};
  keys.forEach((key, i) => {
    out[key] = row.cells[i] ?? "";
  });
  return out;
}

interface Props {
  handleClose: () => void;
  projectId: string;
  sequenceId?: string;
  breakdownId?: string;
  shotList: ShotListType;
  updateRefreshCounter: (breakdownId: string) => void;
  fileId: string;
}

const BulkImportShotsFromCsvModal = ({
  handleClose,
  projectId,
  sequenceId,
  breakdownId,
  shotList,
  updateRefreshCounter,
  fileId,
}: Props) => {
  const classes = useStyles();
  const [csvPasteText, setCsvPasteText] = useState("");
  const [columnHeaders, setColumnHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<DynamicCsvRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addToSameBreakdown, setAddToSameBreakdown] = useState(false);

  const parseCsvString = useCallback((trimmed: string) => {
    Papa.parse<Record<string, string>>(trimmed, {
      header: true,
      skipEmptyLines: "greedy",
      complete: (results: ParseResult<Record<string, string>>) => {
        const rawFields = results.meta.fields ?? [];
        if (rawFields.length === 0) {
          toast.error("Could not detect columns. Add a header row.");
          return;
        }

        const headers = rawFields.map((f, i) => {
          const t = f.replace(/^\uFEFF/, "").trim();
          return t || `Column ${i + 1}`;
        });

        const parsed: DynamicCsvRow[] = [];
        results.data.forEach((rec: Record<string, string>, index: number) => {
          const cleaned = stripBomKeys(rec as Record<string, unknown>) as Record<
            string,
            string | undefined
          >;
          const cells = rawFields.map((origKey) =>
            String(cleaned[origKey] ?? "").trim()
          );
          if (cells.every((c) => c === "")) {
            return;
          }
          parsed.push({
            id: `csv-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`,
            cells,
          });
        });

        if (!parsed.length) {
          toast.error("No data rows found.");
          return;
        }

        setColumnHeaders(headers);
        setRows(parsed);
      },
      error: () => {
        toast.error("Could not parse that text as CSV.");
      },
    });
  }, []);

  useEffect(() => {
    const trimmed = csvPasteText.trim();
    if (!trimmed) {
      setColumnHeaders([]);
      setRows([]);
      return;
    }
    const id = window.setTimeout(() => {
      parseCsvString(csvPasteText);
    }, CSV_PARSE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [csvPasteText, parseCsvString]);

  const updateCell = (id: string, cellIndex: number, value: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = [...r.cells];
        next[cellIndex] = value;
        return { ...r, cells: next };
      })
    );
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleUploadShots = async () => {
    const shotList = rows.map((row) => rowToShotObject(columnHeaders, row));
    const basePayload = {
      projectId,
      sequenceId,
      shotList,
    };

    setIsSubmitting(true);
    try {
      if (addToSameBreakdown && breakdownId) {
       const res = await bulkUploadShotsApiwithBreakdownId(basePayload, breakdownId);
       if (res.status === 200) {
        toast.success("Shots uploaded");
        updateRefreshCounter(breakdownId);
        handleClose();
       } else {
        toast.error("Failed to upload shots");
       }
      } else {
        const res = await bulkUploadShotsApi({ ...basePayload, fileId });
        if (res.status === 200) {
          toast.success("Shots uploaded");
          updateRefreshCounter(res.data.data.breakdownId);
          handleClose();
        } else {
          toast.error("Failed to upload shots");
        }
      }
      toast.success("Shots uploaded");
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload shots");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={classes.bulkImportRoot} sx={{ color: "#606479" }}>
      <Box className={classes.flexBetween}>
        <Typography variant="h4" sx={{ color: "white", fontSize: "1.25rem" }}>
          Add bulk shots
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={{ fontSize: "14px", color: "#F9F9F9" }}>
          Paste CSV text
        </Typography>
        <Typography
          component="div"
          sx={{ fontSize: "12px", color: "#A3A6B7", lineHeight: 1.5 }}
        >
          The first row must contain column headers. Use these exact header names for shot details:
          {" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
            description
          </Box>{" "}
          (required),{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
            characters
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
            cameraPosition
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
            location
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
            dialogue
          </Box>
          . You can also include these optional columns for additional details:
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          shotEmotion
          </Box>{" "}
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          voiceOver
          </Box>{" "}
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          storyboardPrompt
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          shotType
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          rackFocus
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          vfxRequired
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          cameraAngle
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          focalLength
          </Box>
          ,{" "}
          <Box component="span" sx={{ color: "#C8CAD6", fontFamily: "monospace", fontSize: "11px" }}>
          characterAppearance.
          </Box>{" "}
          Download{" "}
          <Link
            component="button"
            type="button"
            onClick={downloadBulkImportExampleCsv}
            underline="always"
            sx={{
              color: "#5B8DEF",
              cursor: "pointer",
              fontSize: "inherit",
              verticalAlign: "baseline",
              p: 0,
              border: "none",
              background: "none",
              fontFamily: "inherit",
            }}
          >
            example.csv
          </Link>
          {" "}
          (headers only).
        </Typography>
        <textarea
          rows={5}
          spellCheck={false}
          autoComplete="off"
          placeholder={
            "description,characters,cameraPosition,location,dialogue\n" +
            "Opening wide,Alice; Bob,wide,Studio,Hello"
          }
          className={classes.bulkImportCsvTextarea}
          value={csvPasteText}
          onChange={(e) => setCsvPasteText(e.target.value)}
        />
      </Box>

      {rows.length > 0 && columnHeaders.length > 0 && (
        <TableContainer className={classes.bulkImportTableWrap}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columnHeaders.map((h, i) => (
                  <TableCell
                    key={`h-${i}-${h}`}
                    sx={{
                      backgroundColor: "#252A38",
                      borderBottom: "1px solid #1B1D28",
                      color: "#F9F9F999",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    backgroundColor: "#252A38",
                    borderBottom: "1px solid #1B1D28",
                    color: "#F9F9F999",
                    fontSize: "12px",
                    fontWeight: 600,
                    width: 48,
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  {columnHeaders.map((_, colIndex) => (
                    <TableCell
                      key={`${row.id}-c-${colIndex}`}
                      align="left"
                      sx={{ borderColor: "#252A38", verticalAlign: "middle" }}
                    >
                      <input
                        className={classes.bulkImportCellInput}
                        value={row.cells[colIndex] ?? ""}
                        onChange={(e) =>
                          updateCell(row.id, colIndex, e.target.value)
                        }
                      />
                    </TableCell>
                  ))}
                  <TableCell
                    align="left"
                    sx={{ borderColor: "#252A38", verticalAlign: "middle" }}
                  >
                    <Tooltip title="Remove row">
                      <IconButton
                        size="small"
                        onClick={() => removeRow(row.id)}
                        sx={{ color: "#A3A6B7" }}
                      >
                        <Trash size={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box
        className={classes.flexCenter}
        sx={{ flexDirection: "column", gap: "12px" }}
      >
        {breakdownId && (
          <FormControlLabel
            sx={{
              margin: 0,
              alignSelf: "center",
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
                color: "#F9F9F9",
              },
            }}
            control={
              <Checkbox
                checked={addToSameBreakdown}
                onChange={(_, checked) => setAddToSameBreakdown(checked)}
                sx={{
                  color: "#606479",
                  "&.Mui-checked": { color: "#6C65D9" },
                }}
              />
            }
            label="Add to same breakdown"
          />
        )}
        <Box className={classes.flexAndGap}>
          <Button
            variant="contained"
            className={classes.addShotButton}
            disabled={isSubmitting || rows.length === 0}
            onClick={handleUploadShots}
          >
            {isSubmitting ? "Creating…" : "Create shots"}
          </Button>
          <Button
            className={classes.cancelButton}
            onClick={() => handleClose()}
            variant="outlined"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkImportShotsFromCsvModal;
